import { Quaternion, Vector3 } from 'three';
import type { Surface } from '../world/Surface';
import {
  projectOnPlane,
  quaternionFromBasis,
  rightFromForwardUp,
  slerpVector,
} from '../utils/math';

export interface LandingPlan {
  surface: Surface;
  /** World position of the body centre once the robot has settled. */
  targetPosition: Vector3;
  /** Body orientation once the robot has settled. */
  targetQuaternion: Quaternion;
  /** Body forward direction on the new surface. */
  forward: Vector3;
  /** Local coordinates of the contact point on the new surface. */
  local: { u: number; v: number };
}

const scratchForward = new Vector3();
const scratchUp = new Vector3();
const scratchRight = new Vector3();
const scratchBack = new Vector3();
const scratchContact = new Vector3();

/**
 * Work out where the body comes to rest when it lands on `surface`.
 * The contact point is the projection of the feet onto the surface plane; the
 * body then sits `attachHeight` above it along the surface normal.
 */
export function planLanding(
  surface: Surface,
  bodyCenter: Vector3,
  currentForward: Vector3,
  attachHeight: number,
): LandingPlan {
  const contact = scratchContact.copy(bodyCenter);
  contact.addScaledVector(surface.normal, -surface.signedDistance(contact));

  const local = surface.toLocal(contact);

  // Keep the heading: project the current forward into the new surface plane.
  const forward = projectOnPlane(currentForward, surface.normal, scratchForward);
  if (forward.lengthSq() < 1e-6) {
    forward.copy(surface.forward);
  } else {
    forward.normalize();
  }

  const right = rightFromForwardUp(forward, surface.normal, scratchRight);
  const back = scratchBack.copy(forward).negate();
  const targetQuaternion = quaternionFromBasis(right, surface.normal, back, new Quaternion());

  const targetPosition = surface.toWorld(local.u, local.v, attachHeight, new Vector3());

  return {
    surface,
    targetPosition,
    targetQuaternion,
    forward: forward.clone(),
    local,
  };
}

/**
 * Blend the body orientation between two frames.
 * The up vector is interpolated separately from the forward vector so that the
 * body rolls around the corner instead of spinning.
 */
export function blendFrames(
  fromUp: Vector3,
  fromForward: Vector3,
  toUp: Vector3,
  toForward: Vector3,
  t: number,
  outQuaternion: Quaternion,
  outUp: Vector3,
  outForward: Vector3,
): void {
  slerpVector(fromUp, toUp, t, outUp);
  slerpVector(fromForward, toForward, t, outForward);
  outForward.addScaledVector(outUp, -outForward.dot(outUp));
  if (outForward.lengthSq() < 1e-8) outForward.copy(toForward);
  outForward.normalize();

  const right = rightFromForwardUp(outForward, outUp, scratchRight);
  const back = scratchBack.copy(outForward).negate();
  quaternionFromBasis(right, outUp, back, outQuaternion);
}

/**
 * Choose the forward direction for a new surface.
 *
 * Preference order:
 *  1. the current forward projected into the new surface plane (keeps direction),
 *  2. the current up projected into the new surface plane (keeps climbing),
 *  3. the new surface's canonical forward.
 */
export function transitionForward(
  currentForward: Vector3,
  currentUp: Vector3,
  targetSurface: Surface,
  out = new Vector3(),
): Vector3 {
  const projected = projectOnPlane(currentForward, targetSurface.normal, out);
  if (projected.lengthSq() > 1e-4) return projected.normalize();

  const climb = projectOnPlane(currentUp, targetSurface.normal, scratchUp);
  if (climb.lengthSq() > 1e-4) return out.copy(climb).normalize();

  return out.copy(targetSurface.forward);
}
